import { z } from 'zod';
import {
  DocumentOwnerType,
  DocumentType,
  JobStatus,
  Permission,
  UserRole,
  UserStatus,
  VerificationStatus,
} from '../enums';
import { booleanish, dateSchema, paginationSchema, zEnum } from './common';
import { seekerSearchSchema } from './seeker';

// ---------------------------------------------------------------------------
// Employer approval queue
// ---------------------------------------------------------------------------

export const adminCompanySearchSchema = paginationSchema.extend({
  q: z.string().trim().max(120).optional(),
  verificationStatus: zEnum(VerificationStatus).optional(),
  isBlocked: booleanish.optional(),
  state: z.string().trim().max(100).optional(),
});
export type AdminCompanySearchQuery = z.infer<typeof adminCompanySearchSchema>;

// ---------------------------------------------------------------------------
// Vacancy moderation queue
// ---------------------------------------------------------------------------

/**
 * Backs `GET /admin/jobs`.
 *
 * `GET /jobs` only ever returns PUBLISHED rows and `GET /jobs/mine` is scoped
 * to the caller's own company, so before this existed a vacancy submitted for
 * approval was invisible to the people holding `job:approve` — the decision
 * endpoint was reachable but the queue feeding it was not.
 *
 * `status` deliberately has no default: the same screen serves triage
 * (PENDING_APPROVAL) and lookup (any state), and defaulting would quietly hide
 * the rest.
 */
export const adminJobSearchSchema = paginationSchema.extend({
  q: z.string().trim().max(120).optional(),
  status: zEnum(JobStatus).optional(),
  companyId: z.string().uuid().optional(),
  categoryId: z.string().uuid().optional(),
  state: z.string().trim().max(100).optional(),
  /// Submitted more than N days ago — how an ageing queue surfaces its tail.
  submittedBeforeDays: z.coerce.number().int().min(1).max(365).optional(),
});
export type AdminJobSearchQuery = z.infer<typeof adminJobSearchSchema>;

// ---------------------------------------------------------------------------
// Job seeker administration
// ---------------------------------------------------------------------------

export const adminSeekerSearchSchema = seekerSearchSchema.merge(
  paginationSchema.pick({ page: true, limit: true }),
).extend({
  verificationStatus: zEnum(VerificationStatus).optional(),
  isBlocked: booleanish.optional(),
});
export type AdminSeekerSearchQuery = z.infer<typeof adminSeekerSearchSchema>;

/** Shared shape for "approve or reject with a reason" on seekers and documents. */
export const verificationDecisionSchema = z
  .object({
    status: z.enum([VerificationStatus.VERIFIED, VerificationStatus.REJECTED]),
    reason: z.string().trim().max(500).optional(),
  })
  .refine((v) => v.status !== VerificationStatus.REJECTED || !!v.reason?.trim(), {
    // A rejection the candidate cannot act on just generates a support call.
    message: 'Give a reason so the candidate knows what to fix',
    path: ['reason'],
  });
export type VerificationDecisionInput = z.infer<typeof verificationDecisionSchema>;

export const blockEntitySchema = z.object({
  isBlocked: z.boolean(),
  reason: z.string().trim().min(5, 'Give a reason').max(500),
});
export type BlockEntityInput = z.infer<typeof blockEntitySchema>;

export const requestCorrectionSchema = z.object({
  message: z
    .string()
    .trim()
    .min(10, 'Tell the employer what is missing')
    .max(1000),
});
export type RequestCorrectionInput = z.infer<typeof requestCorrectionSchema>;

/**
 * Internal reviewer notes. The route supplies `entityType`/`entityId` from the
 * URL — validating fields nothing reads is the defect this schema deliberately
 * avoids repeating.
 */
export const adminNoteSchema = z.object({
  body: z.string().trim().min(3, 'Write something worth reading').max(2000),
});
export type AdminNoteInput = z.infer<typeof adminNoteSchema>;

// ---------------------------------------------------------------------------
// Document verification queue
// ---------------------------------------------------------------------------

export const documentQueueSchema = paginationSchema.extend({
  status: zEnum(VerificationStatus).default(VerificationStatus.PENDING),
  type: zEnum(DocumentType).optional(),
  ownerType: zEnum(DocumentOwnerType).optional(),
  q: z.string().trim().max(120).optional(),
  /** Certificates and ID cards lapsing within N days — drives the reminder job. */
  expiringInDays: z.coerce.number().int().min(1).max(365).optional(),
});
export type DocumentQueueQuery = z.infer<typeof documentQueueSchema>;

// Master data — jobCategorySchema and skillSchema live in ./job, next to the
// rest of the job vocabulary.

// ---------------------------------------------------------------------------
// Notification templates
// ---------------------------------------------------------------------------

/**
 * Indian SMS is gated by DLT: the operator matches the delivered text against
 * the wording registered under `providerTemplateId`, character for character
 * once variables are substituted. A mismatch is dropped silently *after* the
 * gateway has already returned success — so this has to be editable by an
 * operator, not baked into a deployment.
 */
export const notificationTemplateSchema = z.object({
  subject: z.string().trim().max(200).optional().or(z.literal('')),
  body: z.string().trim().min(1, 'The message body is required').max(2000),
  /** DLT template id (SMS) or approved template name (WhatsApp). */
  providerTemplateId: z.string().trim().max(120).optional().or(z.literal('')),
  variables: z.array(z.string().trim().max(40)).max(20).default([]),
  isActive: z.boolean().default(true),
});
export type NotificationTemplateInput = z.infer<typeof notificationTemplateSchema>;

// ---------------------------------------------------------------------------
// Site content
// ---------------------------------------------------------------------------

export const BANNER_POSITIONS = ['HOME_HERO', 'SEEKER_DASHBOARD', 'JOB_LIST'] as const;

export const bannerSchema = z
  .object({
    title: z.string().trim().min(2).max(150),
    subtitle: z.string().trim().max(300).optional().or(z.literal('')),
    imageKey: z.string().trim().min(3).max(500),
    linkUrl: z.string().trim().max(500).optional().or(z.literal('')),
    position: z.enum(BANNER_POSITIONS).default('HOME_HERO'),
    displayOrder: z.coerce.number().int().min(0).max(999).default(0),
    isActive: z.boolean().default(true),
    startsAt: dateSchema.optional(),
    endsAt: dateSchema.optional(),
  })
  .refine((v) => !v.startsAt || !v.endsAt || v.endsAt >= v.startsAt, {
    message: 'End date cannot be before the start date',
    path: ['endsAt'],
  });
export type BannerInput = z.infer<typeof bannerSchema>;

export const contentPageSchema = z.object({
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .min(2)
    .max(120)
    .regex(/^[a-z0-9-]+$/, 'Use lowercase letters, numbers and hyphens only'),
  title: z.string().trim().min(2).max(200),
  body: z.string().trim().min(1).max(100_000),
  isPublished: z.boolean().default(false),
});
export type ContentPageInput = z.infer<typeof contentPageSchema>;

// ---------------------------------------------------------------------------
// Staff & audit
// ---------------------------------------------------------------------------

export const staffSearchSchema = paginationSchema.extend({
  q: z.string().trim().max(120).optional(),
  role: z.enum([UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.STAFF]).optional(),
  status: zEnum(UserStatus).optional(),
});
export type StaffSearchQuery = z.infer<typeof staffSearchSchema>;

export const updateStaffSchema = z.object({
  fullName: z.string().trim().min(2).max(120).optional(),
  role: z.enum([UserRole.ADMIN, UserRole.STAFF]).optional(),
  status: z.enum([UserStatus.ACTIVE, UserStatus.SUSPENDED]).optional(),
  /** Replaces the whole grant list — omit to leave permissions untouched. */
  permissions: z.array(zEnum(Permission)).max(50).optional(),
});
export type UpdateStaffInput = z.infer<typeof updateStaffSchema>;

export const auditSearchSchema = paginationSchema.extend({
  actorId: z.string().uuid().optional(),
  action: z.string().trim().max(80).optional(),
  entityType: z.string().trim().max(60).optional(),
  entityId: z.string().uuid().optional(),
  from: dateSchema.optional(),
  to: dateSchema.optional(),
});
export type AuditSearchQuery = z.infer<typeof auditSearchSchema>;

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export const REPORT_KEYS = [
  'company-manpower',
  'vacancy-fulfilment',
  'candidate-applications',
  'interview-selection',
  'joining-deployment',
] as const;
export type ReportKey = (typeof REPORT_KEYS)[number];

export const REPORT_LABEL: Record<ReportKey, string> = {
  'company-manpower': 'Company-wise manpower',
  'vacancy-fulfilment': 'Vacancy vs fulfilment',
  'candidate-applications': 'Candidate applications',
  'interview-selection': 'Interviews & selection',
  'joining-deployment': 'Joining & deployment',
};

export const reportQuerySchema = z.object({
  from: dateSchema.optional(),
  to: dateSchema.optional(),
  companyId: z.string().uuid().optional(),
  /** `xlsx` streams a workbook; `json` returns rows for the on-screen table. */
  format: z.enum(['json', 'xlsx']).default('json'),
  limit: z.coerce.number().int().min(1).max(10_000).default(1000),
});
export type ReportQuery = z.infer<typeof reportQuerySchema>;
